import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { z } from 'zod';

import { api, type RequestOptions } from '../api/client';

/**
 * TanStack Query Helpers
 *
 * Wraps existing ApiClient to preserve all features:
 * - Correlation ID generation for request tracing
 * - Configurable timeouts
 * - Zod schema validation
 * - AbortController integration via TanStack Query's signal
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 * @added DOC-0032 (2025-12-15)
 */

/**
 * Options for creating a type-safe query.
 */
export interface CreateQueryOptions<TData> {
  /** Unique key for cache management */
  queryKey: QueryKey;
  /** API endpoint path (e.g., "/health") */
  path: string;
  /**
   * Optional runtime schema for response validation. Generated OpenAPI types are the authority for
   * published contracts; a query may omit this only when it consumes those generated types directly.
   */
  schema?: z.ZodType<TData>;
  /** Optional request configuration (headers, timeout, params) */
  requestOptions?: Omit<RequestOptions<never, TData>, 'schema' | 'signal'>;
  /** Query-specific options (staleTime, gcTime, etc.) */
  staleTime?: number;
  gcTime?: number;
  enabled?: boolean;
  refetchOnMount?: boolean | 'always';
  refetchOnReconnect?: boolean | 'always';
  refetchOnWindowFocus?: boolean | 'always';
  /** Disable implicit retries when an operator must see and acknowledge a diagnostic failure. */
  retry?: boolean | number;
  responseCacheTtlMs?: number;
  /**
   * Whether the query consumes TanStack Query's cancellation signal.
   * Disable only for idempotent reads whose viewer/session is part of the query key.
   * ApiClient request timeouts remain active either way.
   */
  consumeAbortSignal?: boolean;
}

type ResponseCacheEntry<TData> = {
  expiresAt: number;
  promise?: Promise<TData>;
  value?: TData;
};

const responseCache = new Map<string, ResponseCacheEntry<unknown>>();
const queryDataReuseCache = new Map<string, ResponseCacheEntry<unknown>>();
const SESSION_RESPONSE_CACHE_PREFIX = 'mm:query-response:';

function queryReuseCacheKey(queryKey: QueryKey) {
  return JSON.stringify(queryKey);
}

function responseCacheKey(
  path: string,
  requestOptions?: Omit<RequestOptions<never>, 'schema' | 'signal'>
) {
  return JSON.stringify([path, requestOptions?.params ?? null]);
}

function getSessionStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function clearQueryResponseCache() {
  responseCache.clear();
  queryDataReuseCache.clear();

  const storage = getSessionStorage();
  if (!storage) return;

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (key?.startsWith(SESSION_RESPONSE_CACHE_PREFIX)) {
      storage.removeItem(key);
    }
  }
}

/**
 * Create type-safe query options using existing ApiClient.
 *
 * Preserves all ApiClient features:
 * - Correlation IDs for debugging
 * - Configurable timeouts
 * - Zod schema validation
 * - Automatic abort handling via Query's signal
 *
 * @example
 * ```ts
 * const healthQuery = createQuery({
 *   queryKey: queryKeys.health.status(),
 *   path: "/health",
 *   schema: healthSchema,
 * });
 *
 * // In component:
 * const { data, isLoading } = useQuery(healthQuery);
 * ```
 */
/**
 * Create a query configuration for TanStack Query.
 *
 * Note: This is a factory function where queryKey, path, schema, and requestOptions
 * are stable configuration values provided by the caller. The caller is responsible
 * for constructing a complete queryKey via query-keys.ts.
 */
export function createQuery<TData>(options: CreateQueryOptions<TData>) {
  const {
    queryKey,
    path,
    schema,
    requestOptions,
    staleTime,
    gcTime,
    enabled,
    refetchOnMount,
    refetchOnReconnect,
    refetchOnWindowFocus,
    retry,
    responseCacheTtlMs,
    consumeAbortSignal = true,
  } = options;
  const cacheKey =
    responseCacheTtlMs !== undefined && responseCacheTtlMs > 0
      ? responseCacheKey(path, requestOptions)
      : null;
  const reuseCacheKey =
    responseCacheTtlMs !== undefined && responseCacheTtlMs > 0
      ? queryReuseCacheKey(queryKey)
      : null;

  const rememberValue = (value: TData) => {
    if (reuseCacheKey !== null && responseCacheTtlMs !== undefined && responseCacheTtlMs > 0) {
      queryDataReuseCache.set(reuseCacheKey, {
        expiresAt: Date.now() + responseCacheTtlMs,
        value,
      });
    }
    return value;
  };

  /* eslint-disable @tanstack/query/exhaustive-deps */
  // Disabled: This is a factory function where queryKey, path, schema, and requestOptions
  // are stable configuration values provided by the caller. The queryKey is constructed
  // by the caller via query-keys.ts with all relevant cache-busting values.
  return queryOptions({
    queryKey,
    queryFn: async (context) => {
      if (cacheKey !== null) {
        const cacheTtlMs = responseCacheTtlMs ?? 0;
        const now = Date.now();
        const cached = responseCache.get(cacheKey) as ResponseCacheEntry<TData> | undefined;

        if (cached && cached.expiresAt > now) {
          if (cached.value !== undefined) return rememberValue(cached.value);
          if (cached.promise) return rememberValue(await cached.promise);
        }

        const promise = api.get<TData>(path, {
          ...requestOptions,
          schema,
          signal: undefined,
        });
        responseCache.set(cacheKey, {
          expiresAt: now + cacheTtlMs,
          promise,
        });

        try {
          const value = await promise;
          const entry = {
            expiresAt: Date.now() + cacheTtlMs,
            value,
          };
          responseCache.set(cacheKey, entry);
          return rememberValue(value);
        } catch (error) {
          responseCache.delete(cacheKey);
          if (reuseCacheKey !== null) {
            queryDataReuseCache.delete(reuseCacheKey);
          }
          throw error;
        }
      }

      return rememberValue(
        await api.get<TData>(path, {
          ...requestOptions,
          schema,
          signal: consumeAbortSignal ? context.signal : undefined,
        })
      );
    },
    staleTime,
    gcTime,
    enabled,
    refetchOnMount,
    refetchOnReconnect,
    refetchOnWindowFocus,
    ...(retry === undefined ? {} : { retry }),
  });
  /* eslint-enable @tanstack/query/exhaustive-deps */
}

export function useCachedQuery<TData>(options: ReturnType<typeof createQuery<TData>>) {
  const queryClient = useQueryClient();
  const queryEnabled = options.enabled ?? true;
  const effectiveQueryKey = queryEnabled
    ? options.queryKey
    : ([...options.queryKey, { disabled: true }] as const);
  const state = queryClient.getQueryState<TData>(options.queryKey);
  const staleTimeMs = typeof options.staleTime === 'number' ? options.staleTime : 0;
  const fallback = queryDataReuseCache.get(queryReuseCacheKey(options.queryKey)) as
    | ResponseCacheEntry<TData>
    | undefined;
  const hasReusableFallback =
    fallback?.value !== undefined && fallback.expiresAt > Date.now() && staleTimeMs > 0;
  const hasReusableData =
    queryEnabled &&
    ((state?.data !== undefined &&
      !state.isInvalidated &&
      staleTimeMs > 0 &&
      Date.now() - state.dataUpdatedAt < staleTimeMs) ||
      hasReusableFallback);
  const initialData = state?.data ?? (hasReusableFallback ? fallback.value : undefined);
  const initialDataUpdatedAt =
    state?.data !== undefined ? state.dataUpdatedAt : hasReusableFallback ? Date.now() : undefined;

  return useQuery({
    ...options,
    queryKey: effectiveQueryKey,
    enabled: queryEnabled && !hasReusableData,
    initialData: hasReusableData ? initialData : undefined,
    initialDataUpdatedAt: hasReusableData ? initialDataUpdatedAt : undefined,
  });
}

/**
 * Options for creating a mutation function.
 */
export interface CreateMutationFnOptions<TData> {
  /** API endpoint path */
  path: string;
  /** HTTP method */
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Zod schema for response validation */
  schema: z.ZodType<TData>;
  /** Optional request configuration (headers, timeout) */
  requestOptions?: Omit<RequestOptions<unknown, TData>, 'schema' | 'body'>;
}

/**
 * Create mutation function using existing ApiClient.
 *
 * Preserves all ApiClient features:
 * - Correlation IDs for debugging
 * - Configurable timeouts
 * - Zod schema validation
 *
 * @example
 * ```ts
 * const createCourseMutation = useMutation({
 *   mutationFn: createMutationFn<CourseResponse, CreateCourseInput>({
 *     path: "/courses",
 *     method: "POST",
 *     schema: courseSchema,
 *   }),
 *   onSuccess: () => {
 *     queryClient.invalidateQueries({ queryKey: queryKeys.courses.all });
 *   },
 * });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- TVariables is needed for caller type inference
export function createMutationFn<TData, TVariables>(
  options: CreateMutationFnOptions<TData>
): (variables: TVariables) => Promise<TData> {
  return async (variables: TVariables): Promise<TData> => {
    const requestOpts = {
      ...options.requestOptions,
      schema: options.schema,
    };

    let response: TData;
    switch (options.method) {
      case 'POST':
        response = await api.post<TData, TVariables>(options.path, variables, requestOpts);
        break;
      case 'PUT':
        response = await api.put<TData, TVariables>(options.path, variables, requestOpts);
        break;
      case 'PATCH':
        response = await api.patch<TData, TVariables>(options.path, variables, requestOpts);
        break;
      case 'DELETE':
        response = await api.delete<TData>(options.path, requestOpts);
        break;
    }

    clearQueryResponseCache();
    return response;
  };
}

/**
 * Create mutation function for DELETE operations (no body).
 *
 * @example
 * ```ts
 * const deleteCourse = useMutation({
 *   mutationFn: createDeleteMutationFn({
 *     path: "/courses",
 *     schema: successSchema,
 *   }),
 * });
 *
 * // Usage: deleteCourse.mutate(courseId)
 * ```
 */
export function createDeleteMutationFn<TData>(options: {
  /** Base path - ID will be appended */
  path: string;
  /** Zod schema for response validation */
  schema: z.ZodType<TData>;
  /** Optional request configuration */
  requestOptions?: Omit<RequestOptions<never, TData>, 'schema'>;
}): (id: string) => Promise<TData> {
  return async (id: string): Promise<TData> => {
    const response = await api.delete<TData>(`${options.path}/${id}`, {
      ...options.requestOptions,
      schema: options.schema,
    });
    clearQueryResponseCache();
    return response;
  };
}
